import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  contactsApi,
  defaultSearchQuery,
} from '@texas/api/endpoints/contactsApi';
import { offerApi } from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { Icons } from '@texas/components/shared/Icons';
import { ReactSelectOption } from '@texas/types';
import { formatDate, toISOString } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  variantId: number;
  tierPriceIds?: number[];
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function MakeOfferedOfferDrawer({
  variantId,
  tierPriceIds,
  isOpen,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('offer.makeOffer')}</DrawerHeader>
        {isOpen && tierPriceIds && (
          <MakeOfferedOfferDrawerInner
            tierPriceIds={tierPriceIds}
            variantId={variantId}
            onClose={onClose}
            onCreated={onCreated}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

interface InnerProps {
  variantId: number;
  tierPriceIds: number[];
  onClose: () => void;
  onCreated: () => void;
}

function MakeOfferedOfferDrawerInner({
  variantId,
  tierPriceIds,
  onClose,
  onCreated,
}: InnerProps) {
  const { t } = useTranslation();
  const {
    data: preview,
    loading,
    error,
    refetch,
  } = useApiResource(offerApi.previewOfferedOffer);
  const {
    request: createOfferedOffer,
    loading: creating,
    error: createError,
  } = useApiRequest(offerApi.createOfferedOffer);
  const {
    request: appendOfferedOfferTierPrices,
    loading: appending,
    error: appendError,
  } = useApiRequest(offerApi.appendOfferedOfferTierPrices);
  const {
    data: offeredOffers,
    loading: offeredOffersLoading,
    refetch: refetchOfferedOffers,
  } = useApiResource(offerApi.getOfferedOffersByVariant);
  const {
    data: contacts,
    loading: contactsLoading,
    refetch: refetchContacts,
  } = useApiResource(contactsApi.getAll);
  const [selectedOfferedOfferId, setSelectedOfferedOfferId] = useState('new');
  const [carryOverOfferedOfferId, setCarryOverOfferedOfferId] = useState('');
  const [expiresOn, setExpiresOn] = useState(getDefaultExpiresOn());
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );
  const replacePricesDialog = useDisclosure();
  const tierPriceIdsKey = tierPriceIds.join(',');

  useEffect(() => {
    if (tierPriceIds.length > 0) {
      refetch({
        tierPriceIds,
        carryOverOfferedOfferId:
          selectedOfferedOfferId === 'new' && carryOverOfferedOfferId
            ? parseInt(carryOverOfferedOfferId, 10)
            : null,
      });
      refetchOfferedOffers(variantId);
    }
  }, [
    carryOverOfferedOfferId,
    refetch,
    refetchOfferedOffers,
    selectedOfferedOfferId,
    tierPriceIds,
    tierPriceIdsKey,
    variantId,
  ]);

  useEffect(() => {
    if (!preview) return;

    refetchContacts({
      ...defaultSearchQuery,
      customerId: preview.customerId,
      pageSize: 100,
    });
  }, [preview, refetchContacts]);

  useEffect(() => {
    if (!preview || selectedContactId != null) return;

    setSelectedContactId(preview.defaultContactId ?? null);
  }, [preview, selectedContactId]);

  const matchingOfferedOffers = useMemo(
    () =>
      (offeredOffers ?? []).filter(
        (offeredOffer) => offeredOffer.offerGroupId === preview?.offerGroupId,
      ),
    [offeredOffers, preview?.offerGroupId],
  );

  const selectedOfferedOffer = useMemo(
    () =>
      matchingOfferedOffers.find(
        (offeredOffer) => String(offeredOffer.id) === selectedOfferedOfferId,
      ) ?? null,
    [matchingOfferedOffers, selectedOfferedOfferId],
  );

  const contactOptions = useMemo<ReactSelectOption[]>(
    () =>
      (contacts?.items ?? []).map((contact) => ({
        value: contact.id,
        label: `${contact.name} (${contact.email})`,
      })),
    [contacts?.items],
  );

  const selectedContactOption = useMemo(
    () =>
      contactOptions.find((option) => option.value === selectedContactId) ??
      null,
    [contactOptions, selectedContactId],
  );
  const expiresInDays = useMemo(
    () => getDayDiffFromToday(expiresOn),
    [expiresOn],
  );
  const pricesToAdd = useMemo(
    () =>
      preview?.branches.flatMap((branch) =>
        branch.currencies.flatMap((currency) => currency.tierPrices),
      ) ?? [],
    [preview],
  );
  const showCarryOverOfferNumberColumn =
    pricesToAdd.length > 0 &&
    pricesToAdd.every((tierPrice) => !!tierPrice.carryOverOfferNumber);
  const showPreviousExpiresColumn =
    pricesToAdd.length > 0 &&
    pricesToAdd.every((tierPrice) => !!tierPrice.carryOverExpiresOn);

  useEffect(() => {
    if (
      selectedOfferedOfferId !== 'new' &&
      !matchingOfferedOffers.some(
        (offeredOffer) => String(offeredOffer.id) === selectedOfferedOfferId,
      )
    ) {
      setSelectedOfferedOfferId('new');
    }
  }, [matchingOfferedOffers, selectedOfferedOfferId]);

  const replacementRows = useMemo(() => {
    if (!preview || !selectedOfferedOffer) return [];

    return preview.branches.flatMap((branch) =>
      branch.currencies.flatMap((currency) =>
        currency.tierPrices
          .map((tierPrice) => {
            const existing = selectedOfferedOffer.branches
              .find((b) => b.branchId === branch.branchId)
              ?.currencies.find((c) => c.currencyId === currency.currencyId)
              ?.tierPrices.find(
                (existingTierPrice) =>
                  existingTierPrice.id !== tierPrice.id &&
                  existingTierPrice.quantity === tierPrice.quantity &&
                  existingTierPrice.matrixNodeValueXName ===
                    tierPrice.matrixNodeValueXName &&
                  existingTierPrice.matrixNodeValueYName ===
                    tierPrice.matrixNodeValueYName,
              );
            return existing
              ? {
                  id: tierPrice.id,
                  branchIdentifier: branch.branchIdentifier,
                  currencyCode: currency.currencyCode,
                  quantity: tierPrice.quantity,
                  oldPrice: existing.price,
                  newPrice: tierPrice.price,
                  oldAcceptedDate: existing.acceptedDate,
                  oldAcceptedByUserName: existing.acceptedByUserName,
                }
              : null;
          })
          .filter(
            (
              row,
            ): row is {
              id: number;
              branchIdentifier: string;
              currencyCode: string;
              quantity: number;
              oldPrice: number | null;
              newPrice: number | null;
              oldAcceptedDate: string | null;
              oldAcceptedByUserName: string | null;
            } => !!row,
          ),
      ),
    );
  }, [preview, selectedOfferedOffer]);

  const submitOfferedOffer = async (replaceExisting: boolean) => {
    if (selectedOfferedOffer) {
      const response = await appendOfferedOfferTierPrices(
        selectedOfferedOffer.id,
        {
          tierPriceIds,
          replaceExisting,
        },
      );
      if (!response.hasError) {
        onCreated();
        onClose();
      }
      return;
    }

    if (selectedContactId == null) return;

    const response = await createOfferedOffer({
      tierPriceIds,
      contactId: selectedContactId,
      carryOverOfferedOfferId: carryOverOfferedOfferId
        ? parseInt(carryOverOfferedOfferId, 10)
        : null,
      expiresOn: toISOString(expiresOn),
    });
    if (!response.hasError) {
      onCreated();
      onClose();
    }
  };

  const handleCreate = () => {
    if (selectedOfferedOffer && replacementRows.length > 0) {
      replacePricesDialog.onOpen();
      return;
    }

    submitOfferedOffer(false);
  };

  return (
    <>
      <DrawerBody>
        <Flex direction="column" gap={4}>
          {loading && <Spinner />}
          {error && <ErrorDetails error={error} />}
          {createError && <ErrorDetails error={createError} />}
          {appendError && <ErrorDetails error={appendError} />}
          {preview && (
            <>
              <Text fontSize="sm" color="gray.400">
                {preview.customerName}
              </Text>
              <Flex direction="column" gap={1}>
                <Text fontSize="xs" color="gray.400">
                  {t('offer.offerOfferedOffer')}
                </Text>
                <Select
                  size="sm"
                  value={selectedOfferedOfferId}
                  isDisabled={offeredOffersLoading}
                  onChange={(e) => setSelectedOfferedOfferId(e.target.value)}
                >
                  <option value="new">
                    {t('offer.createNewOfferedOffer')}
                  </option>
                  {matchingOfferedOffers.map((offeredOffer) => (
                    <option
                      key={offeredOffer.id}
                      value={String(offeredOffer.id)}
                    >
                      {offeredOffer.offerNumber} · {offeredOffer.tierPriceCount}{' '}
                      {t('offer.prices')}
                    </option>
                  ))}
                </Select>
              </Flex>
              {!selectedOfferedOffer && (
                <>
                  {matchingOfferedOffers.length > 0 && (
                    <Flex direction="column" gap={1}>
                      <Text fontSize="xs" color="gray.400">
                        {t('offer.carryOverPricesFrom')}
                      </Text>
                      <Select
                        size="sm"
                        value={carryOverOfferedOfferId}
                        isDisabled={offeredOffersLoading}
                        onChange={(e) =>
                          setCarryOverOfferedOfferId(e.target.value)
                        }
                      >
                        <option value="">{t('general.none')}</option>
                        {matchingOfferedOffers.map((offeredOffer) => (
                          <option
                            key={offeredOffer.id}
                            value={String(offeredOffer.id)}
                          >
                            {offeredOffer.offerNumber} ·{' '}
                            {offeredOffer.tierPriceCount} {t('offer.prices')}
                          </option>
                        ))}
                      </Select>
                      {carryOverOfferedOfferId && (
                        <Text fontSize="xs" color="gray.500">
                          {t('offer.carryOverPricesFromHint')}
                        </Text>
                      )}
                    </Flex>
                  )}
                  <FormControl isRequired={true}>
                    <FormLabel fontSize="sm">
                      {t('offer.offeredOfferContact')}
                    </FormLabel>
                    <TexasSelect<ReactSelectOption>
                      isClearable={false}
                      isLoading={contactsLoading}
                      placeholder={t('offer.selectOfferedOfferContact')}
                      value={selectedContactOption}
                      onChange={(option) =>
                        setSelectedContactId(option?.value ?? null)
                      }
                      options={contactOptions}
                    />
                  </FormControl>
                  <FormControl isRequired={true}>
                    <FormLabel fontSize="sm">{t('offer.expiresOn')}</FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={expiresOn}
                      onChange={(e) => setExpiresOn(e.target.value)}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {t('offer.expiresInDays', { count: expiresInDays })}
                    </Text>
                    <ExpirationDateButtons onDateSelect={setExpiresOn} />
                  </FormControl>
                </>
              )}
              {selectedOfferedOffer && replacementRows.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {t('offer.replaceOfferedOfferPricesWarning', {
                      count: replacementRows.length,
                    })}
                  </Text>
                </Alert>
              )}
              {selectedOfferedOffer && (
                <Flex direction="column" gap={3}>
                  <Text fontWeight="semibold" fontSize="sm">
                    {t('offer.currentOfferedOfferPrices')}
                  </Text>
                  {selectedOfferedOffer.branches.map((branch) => (
                    <Flex key={branch.branchId} direction="column" gap={2}>
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {branch.branchName}
                        </Text>
                        <Badge>{branch.branchIdentifier}</Badge>
                      </Flex>
                      {branch.currencies.map((currency) => {
                        const showMatrixColumn = currency.tierPrices.some(
                          (tierPrice) =>
                            tierPrice.matrixNodeValueXName != null ||
                            tierPrice.matrixNodeValueYName != null,
                        );

                        return (
                          <TableContainer key={currency.currencyId}>
                            <Table size="sm" variant="simple">
                              <Thead>
                                <Tr>
                                  {showMatrixColumn && (
                                    <Th>{currency.currencyCode}</Th>
                                  )}
                                  <Th>{t('general.quantity')}</Th>
                                  <Th>{t('inquiry.price')}</Th>
                                  <Th>{t('general.status')}</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {currency.tierPrices.map((tierPrice) => (
                                  <Tr key={tierPrice.id}>
                                    {showMatrixColumn && (
                                      <Td>
                                        <Flex gap={1} wrap="wrap">
                                          {tierPrice.matrixNodeValueYName && (
                                            <Tag size="sm">
                                              {tierPrice.matrixNodeValueYName}
                                            </Tag>
                                          )}
                                          {tierPrice.matrixNodeValueXName && (
                                            <Tag size="sm">
                                              {tierPrice.matrixNodeValueXName}
                                            </Tag>
                                          )}
                                        </Flex>
                                      </Td>
                                    )}
                                    <Td>{tierPrice.quantity}</Td>
                                    <Td>
                                      {tierPrice.price != null
                                        ? `${tierPrice.price} ${currency.currencyCode}`
                                        : '—'}
                                    </Td>
                                    <Td>
                                      {tierPrice.acceptedDate ? (
                                        <Badge colorScheme="green">
                                          {t('general.accepted')}
                                        </Badge>
                                      ) : (
                                        <Badge colorScheme="gray">
                                          {t('offer.pending')}
                                        </Badge>
                                      )}
                                      {tierPrice.acceptedByUserName && (
                                        <Text fontSize="2xs" color="gray.400">
                                          {tierPrice.acceptedByUserName}
                                        </Text>
                                      )}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        );
                      })}
                    </Flex>
                  ))}
                </Flex>
              )}
              <Text fontWeight="semibold" fontSize="sm">
                {t('offer.pricesToAdd')}
              </Text>
              {preview.branches.map((branch) => (
                <Flex key={branch.branchId} direction="column" gap={2}>
                  <Flex align="center" gap={2}>
                    <Text fontWeight="semibold" fontSize="sm">
                      {branch.branchName}
                    </Text>
                    <Badge>{branch.branchIdentifier}</Badge>
                  </Flex>
                  {branch.currencies.map((currency) => {
                    const showMatrixColumn = currency.tierPrices.some(
                      (tierPrice) =>
                        tierPrice.matrixNodeValueXName != null ||
                        tierPrice.matrixNodeValueYName != null,
                    );

                    return (
                      <TableContainer key={currency.currencyId}>
                        <Table size="sm" variant="simple">
                          <Thead>
                            <Tr>
                              {showMatrixColumn && (
                                <Th>{currency.currencyCode}</Th>
                              )}
                              <Th>{t('general.quantity')}</Th>
                              <Th>{t('inquiry.price')}</Th>
                              {showCarryOverOfferNumberColumn && (
                                <Th>{t('offer.fromOfferNumber')}</Th>
                              )}
                              {showPreviousExpiresColumn && (
                                <Th>{t('offer.previousExpires')}</Th>
                              )}
                              <Th>{t('offer.expireDate')}</Th>
                              {selectedOfferedOffer && (
                                <>
                                  <Th>{t('offer.replacesPrice')}</Th>
                                  <Th>{t('offer.replacesState')}</Th>
                                </>
                              )}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {currency.tierPrices.map((tierPrice) => {
                              const replacingRow = replacementRows.find(
                                (row) => row.id === tierPrice.id,
                              );

                              return (
                                <Tr key={tierPrice.id}>
                                  {showMatrixColumn && (
                                    <Td>
                                      <Flex gap={1} wrap="wrap">
                                        {tierPrice.matrixNodeValueYName && (
                                          <Tag size="sm">
                                            {tierPrice.matrixNodeValueYName}
                                          </Tag>
                                        )}
                                        {tierPrice.matrixNodeValueXName && (
                                          <Tag size="sm">
                                            {tierPrice.matrixNodeValueXName}
                                          </Tag>
                                        )}
                                      </Flex>
                                    </Td>
                                  )}
                                  <Td>{tierPrice.quantity}</Td>
                                  <Td>
                                    {tierPrice.price != null
                                      ? `${tierPrice.price} ${currency.currencyCode}`
                                      : '—'}
                                    {replacingRow && (
                                      <Badge
                                        ml={2}
                                        colorScheme="orange"
                                        fontSize="2xs"
                                      >
                                        {t('offer.replacesExisting')}
                                      </Badge>
                                    )}
                                  </Td>
                                  {showCarryOverOfferNumberColumn && (
                                    <Td>{tierPrice.carryOverOfferNumber}</Td>
                                  )}
                                  {showPreviousExpiresColumn && (
                                    <Td>
                                      {formatDate(
                                        Locale.En,
                                        tierPrice.carryOverExpiresOn!,
                                      )}
                                    </Td>
                                  )}
                                  <Td>
                                    {expiresOn
                                      ? formatDate(
                                          Locale.En,
                                          toISOString(expiresOn)!,
                                        )
                                      : '—'}
                                  </Td>
                                  {selectedOfferedOffer && (
                                    <>
                                      <Td>
                                        {replacingRow?.oldPrice != null
                                          ? `${replacingRow.oldPrice} ${currency.currencyCode}`
                                          : '—'}
                                      </Td>
                                      <Td>
                                        {replacingRow ? (
                                          <>
                                            {replacingRow.oldAcceptedDate ? (
                                              <Badge colorScheme="green">
                                                {t('general.accepted')}
                                              </Badge>
                                            ) : (
                                              <Badge colorScheme="gray">
                                                {t('offer.pending')}
                                              </Badge>
                                            )}
                                            {replacingRow.oldAcceptedByUserName && (
                                              <Text
                                                fontSize="2xs"
                                                color="gray.400"
                                              >
                                                {
                                                  replacingRow.oldAcceptedByUserName
                                                }
                                              </Text>
                                            )}
                                          </>
                                        ) : (
                                          <Text fontSize="sm">—</Text>
                                        )}
                                      </Td>
                                    </>
                                  )}
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    );
                  })}
                </Flex>
              ))}
            </>
          )}
        </Flex>
      </DrawerBody>
      <DrawerFooter gap={2}>
        <Button size="sm" variant="texas-light" onClick={onClose}>
          {t('general.cancel')}
        </Button>
        <Button
          size="sm"
          variant="texas-solid"
          leftIcon={<Icons.Send />}
          isLoading={creating || appending}
          isDisabled={
            !preview ||
            tierPriceIds.length === 0 ||
            (!selectedOfferedOffer && selectedContactId == null)
          }
          onClick={handleCreate}
        >
          {selectedOfferedOffer
            ? t('offer.appendPrices')
            : t('offer.makeOffer')}
        </Button>
        <VerifyDialogWithFunction
          headerTitle={t('offer.replacePrices')}
          isOpen={replacePricesDialog.isOpen}
          onClose={replacePricesDialog.onClose}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('offer.replacePrices')}
          toPerform={() => {
            submitOfferedOffer(true);
          }}
        >
          {t('offer.replaceOfferedOfferPricesConfirm')}
        </VerifyDialogWithFunction>
      </DrawerFooter>
    </>
  );
}

interface ExpirationDateButtonsProps {
  onDateSelect: (date: string) => void;
}

function ExpirationDateButtons({ onDateSelect }: ExpirationDateButtonsProps) {
  const { t } = useTranslation();
  const handleSetDate = (months: number) => {
    onDateSelect(toDateInputValue(addMonthsFromToday(months)));
  };

  return (
    <Flex gap={2} mt={2} wrap="wrap">
      {[1, 2, 3, 6, 12].map((months) => (
        <Button
          key={months}
          size="xs"
          variant="outline"
          onClick={() => handleSetDate(months)}
        >
          {t('offer.expiresInMonths', { count: months })}
        </Button>
      ))}
    </Flex>
  );
}

function getDefaultExpiresOn() {
  return toDateInputValue(addMonthsFromToday(1));
}

function addMonthsFromToday(months: number) {
  const today = new Date();
  const day = today.getDate();
  const date = new Date(today);
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  const lastDayOfTargetMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
  ).getDate();
  date.setDate(Math.min(day, lastDayOfTargetMonth));
  return date;
}

function getDayDiffFromToday(dateString: string) {
  const selectedDate = parseDateInput(dateString);
  if (!selectedDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round(
    (selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
}

function parseDateInput(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
